const CUSTOMER_FACING_CODE_LENGTH = 4;
const MAX_CUSTOMER_FACING_ID_ATTEMPTS = 100;

/**
 * Terms that must not appear in customer-facing random codes.
 *
 * Codes are normalized before matching, so matching is case-insensitive and
 * common numeric substitutions such as 4 -> a and 1 -> i are also caught.
 * Keep entries to terms that can appear within the four-character code.
 */
export const CUSTOMER_FACING_CODE_BLOCKLIST = [
    'anal',
    'anus',
    'arse',
    'ass',
    'boob',
    'cock',
    'coon',
    'crap',
    'cum',
    'cunt',
    'dago',
    'damn',
    'dick',
    'dyke',
    'fag',
    'fuck',
    'gook',
    'homo',
    'jizz',
    'kike',
    'kkk',
    'mong',
    'nazi',
    'nig',
    'paki',
    'pedo',
    'piss',
    'porn',
    'rape',
    'sex',
    'shit',
    'slut',
    'spaz',
    'spic',
    'tard',
    'tit',
    'turd',
    'twat',
    'wank',
    'whor',
    'xxx',
] as const;

const CUSTOMER_FACING_CODE_CHARACTER_SUBSTITUTIONS: Readonly<Record<string, string>> = {
    '0': 'o',
    '1': 'i',
    '2': 'z',
    '3': 'e',
    '4': 'a',
    '5': 's',
    '6': 'g',
    '7': 't',
    '8': 'b',
    '9': 'g',
};

export function normalizeCustomerFacingCode(code: string): string {
    return code
        .toLowerCase()
        .split('')
        .map(character => CUSTOMER_FACING_CODE_CHARACTER_SUBSTITUTIONS[character] ?? character)
        .join('');
}

export function isCustomerFacingCodeAllowed(code: string): boolean {
    const normalizedCode = normalizeCustomerFacingCode(code);
    return !CUSTOMER_FACING_CODE_BLOCKLIST.some(term => normalizedCode.includes(term));
}

/**
 * Generates an id whose final four characters are safe to print for customers.
 * The id itself remains the source of truth for the displayed code and QR data.
 */
export function generateCustomerSafeId(createId: () => string): string {
    for (let attempt = 0; attempt < MAX_CUSTOMER_FACING_ID_ATTEMPTS; attempt++) {
        const id = createId();
        const customerFacingCode = id.slice(-CUSTOMER_FACING_CODE_LENGTH);

        if (isCustomerFacingCodeAllowed(customerFacingCode)) {
            return id;
        }
    }

    throw new Error(`Unable to generate a customer-safe id after ${MAX_CUSTOMER_FACING_ID_ATTEMPTS} attempts`);
}
